// Issue form component
// Reference: spec.md US1 - create issue form

import { useState, FormEvent } from 'react';
import { Input, Textarea, Button, FormField } from '../ui';
import { UnitSelector } from './UnitSelector';
import { CategorySelect } from './CategorySelect';
import { FileUpload } from './FileUpload';
import { api, ApiRequestError } from '../../services/api';
import type { Issue, IssueCategory } from '@propman/shared';

interface IssueFormProps {
  onSuccess: (issue: Issue) => void;
  onCancel?: () => void;
}

interface FormErrors {
  unitId?: string | undefined;
  title?: string | undefined;
  description?: string | undefined;
  category?: string | undefined;
  files?: string | undefined;
  general?: string | undefined;
}

export function IssueForm({ onSuccess, onCancel }: IssueFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unitId, setUnitId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!unitId) {
      newErrors.unitId = 'Please select a unit';
    }

    if (!title.trim()) {
      newErrors.title = 'Please enter a title';
    } else if (title.length > 200) {
      newErrors.title = 'Title must be 200 characters or less';
    }

    if (!description.trim()) {
      newErrors.description = 'Please enter a description';
    }

    if (!category) {
      newErrors.category = 'Please select a category';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Create the issue
      const response = await api.post<{ issue: Issue }>('/issues', {
        unitId,
        title: title.trim(),
        description: description.trim(),
        category: category as IssueCategory,
      });

      const issue = response.issue;

      // Upload attachments if any
      if (files.length > 0) {
        const uploadPromises = files.map((file) =>
          api.uploadFile(`/issues/${issue.id}/attachments`, file).catch((err) => {
            console.error(`Failed to upload ${file.name}:`, err);
            return null; // Don't fail the whole submission for attachment errors
          })
        );

        await Promise.all(uploadPromises);
      }

      onSuccess(issue);
    } catch (err) {
      console.error('Failed to create issue:', err);

      if (err instanceof ApiRequestError) {
        if (err.isValidationError && err.details) {
          const fieldErrors: FormErrors = {};
          Object.entries(err.details).forEach(([field, messages]) => {
            const errorKey = field as keyof FormErrors;
            if (['unitId', 'title', 'description', 'category'].includes(field) && messages[0]) {
              fieldErrors[errorKey] = messages[0];
            }
          });
          setErrors(fieldErrors);
        } else {
          setErrors({ general: err.message });
        }
      } else {
        setErrors({ general: 'An unexpected error occurred. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* General error */}
      {errors.general && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm\" role="alert">
          {errors.general}
        </div>
      )}

      {/* Unit selector */}
      <UnitSelector
        value={unitId}
        onChange={setUnitId}
        error={errors.unitId}
        required
      />

      {/* Category */}
      <CategorySelect
        value={category}
        onChange={setCategory}
        error={errors.category}
        required
      />

      {/* Title */}
      <FormField
        label="Issue Title"
        htmlFor="title"
        error={errors.title}
        required
        hint="Brief summary of the issue (max 200 characters)"
      >
        <Input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Leaking tap in bathroom"
          maxLength={200}
          error={!!errors.title}
          required
        />
        <div className="mt-1 text-xs text-gray-500 text-right">
          {title.length}/200
        </div>
      </FormField>

      {/* Description */}
      <FormField
        label="Description"
        htmlFor="description"
        error={errors.description}
        required
        hint="Provide details about the issue, when it started, and any relevant information"
      >
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Please describe the issue in detail..."
          rows={5}
          error={!!errors.description}
          required
        />
      </FormField>

      {/* File upload */}
      <FileUpload
        files={files}
        onChange={setFiles}
        error={errors.files}
      />

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          Submit Issue
        </Button>
      </div>
    </form>
  );
}

export default IssueForm;
